// insuranceAutosave.js — U2-INS C5 (TEST 3.09): Profile → Insurance saves itself, and nothing it
// sends can undo what the server wrote meanwhile. Import-free, so
// scripts/check-insurance-autosave.mjs executes it.
//
// ★★★ WHY A QUEUE. Every request this screen makes that WRITES the insurance column is either
//   this screen's own save (PUT /profile, a shallow merge of the keys sent) or a server route
//   that reads the column, waits on Stedi (up to 24 s), and writes back:
//     * discovery (rcm_service.discover_insurance): since PROD-418 (live v243.269.0) it writes
//       ONLY its key onto a fresh read, so a save made during the Stedi call survives;
//     * the practice eligibility check (direct_test_eligibility): last_eligibility onto a fresh
//       read;
//     * the secondary save (set_secondary_insurance) and its eligibility check
//       (check_secondary_eligibility): the WHOLE column as it was read BEFORE the Stedi call
//       (PROD-443 / PROD-452, B9, not live) — a save made during that call is reverted.
//   And PUT /profile itself reads, merges and PATCHes (update_profile), so a server write that
//   lands inside it is reverted by it. The one shape that loses nothing whatever the server
//   does is the one where this screen never has two of its own requests in flight: each waits
//   for the one before. createSerialQueue is that, and every insurance request the screen makes
//   (the autosave, Save, Re-check, Search, and the secondary card's saves and search) runs
//   through the same queue.
//
// ★★ WHAT THE SAVE CARRIES. The form's own keys, never a key a server route owns
//   (SERVER_OWNED_INSURANCE_KEYS): the shallow merge then leaves those to the server, so a
//   discovery result written between two saves is never sent back stale.

export const AUTOSAVE_MS = 1500

// Written by server routes (discovery, eligibility, the secondary route), never by this form.
export const SERVER_OWNED_INSURANCE_KEYS = Object.freeze(['discovery_result', 'discoveryResult', 'secondary_discovery_result',
  'secondary', 'last_eligibility', 'lastEligibility', 'last_eligibility_secondary'])

// handleSave's own checks, as one function (the autosave's `completed` is whether they pass).
//   'coverage' | 'carrier' | 'member' | null
export function insuranceFormProblem(form, coverageTypes) {
  const f = form || {}
  if (!f.coverageType) return 'coverage'
  const ct = (coverageTypes || []).find((c) => c.id === f.coverageType)
  if (ct?.needsCarrier && !f.provider) return 'carrier'
  if (ct?.needsCarrier && !f.memberId) return 'member'
  return null
}

// What PUT /profile carries for the insurance section. `complete`: true when Save's checks pass
// (the section is marked completed), false when they fail (it is marked NOT completed), left out
// (no `completed` key) when the caller does not say.
// ★ U2 push 2 review r1 m1: an autosave of a form that fails the checks used to leave the stored
//   flag alone, so "Change" (the carrier cleared) kept completed: true on a record with no
//   carrier, and the profile counted the insurance section as done. The flag now follows the
//   form the autosave sends.
export function insuranceSaveBody(form, { complete } = {}) {
  const body = { ...(form || {}), insurerName: form?.provider, planType: form?.coverageType, copayOfficeVisit: form?.copayPCP }
  for (const k of SERVER_OWNED_INSURANCE_KEYS) delete body[k]
  delete body.completed
  if (complete === true) body.completed = true
  else if (complete === false) body.completed = false
  return body
}

// The identity of a save: the body without `completed`, as JSON. Two forms with the same key
// are the same save.
export function insuranceSaveKey(form) {
  return JSON.stringify(insuranceSaveBody(form))
}

// One task at a time, in the order run() was called. A task that throws or rejects does not stop
// the next one; its own promise still rejects for its caller.
export function createSerialQueue() {
  let tail = Promise.resolve()
  let pending = 0
  const run = (task) => {
    pending++
    const p = tail.then(() => task()).finally(() => { pending-- })
    tail = p.catch(() => {})
    return p
  }
  return { run, get pending() { return pending } }
}
